/**
 * Submission success page (Phase 2).
 * The submitter lands here after a successful POST /api/submissions.
 * Loud aesthetic, brand voice.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

const INSTAGRAM_URL = 'https://www.instagram.com/isaidwhatisaidpod/';

export default function SubmitSuccess() {
  return (
    <div className="paper-grain min-h-full bg-cream text-burgundy font-hanken text-[17px] leading-[1.55] overflow-x-hidden">
      {/* ---------- Top brand bar ---------- */}
      <div className="relative z-[2] bg-forest px-7 py-[22px]">
        <div
          className="font-rye text-cream-bright text-center tracking-[1px] leading-none"
          style={{ fontSize: 'clamp(22px, 3vw, 32px)' }}
        >
          <DotI>I</DotI> SAID WHAT <DotI>I</DotI> SAID
        </div>
      </div>

      {/* ---------- Main card ---------- */}
      <main className="relative z-[2] mx-auto max-w-[680px] px-5 pt-16 pb-24">
        <div className="relative bg-cream-bright border-[3px] border-burgundy rounded-3xl p-2 shadow-chunk-8">
          {/* Tape label */}
          <div className="absolute -top-[18px] left-1/2 -translate-x-1/2 -rotate-2 bg-pink text-cream-bright font-bagel px-[22px] py-[6px] text-[14px] tracking-[1.5px] border-2 border-burgundy shadow-chunk-2 whitespace-nowrap">
            CAUGHT
          </div>

          <div className="px-6 pt-12 pb-10 sm:px-12 sm:pt-14 sm:pb-12 text-center">
            <h1
              className="font-bagel text-burgundy mb-4 leading-[0.95] tracking-[-0.01em]"
              style={{ fontSize: 'clamp(40px, 7vw, 72px)' }}
            >
              GOT IT,
              <br />
              <span className="text-pink">THANK YOU.</span>
            </h1>

            <p className="text-burgundy-soft font-medium text-[17px] sm:text-[18px] mb-3 max-w-[480px] mx-auto">
              Got it. Now go touch grass. Your dilemma is in "safe hands"
            </p>
            <p className="text-burgundy-faint text-[14px] mb-10 max-w-[480px] mx-auto">
              If we pick it up, you'll hear about it. If we don't, we're still rooting for you.
            </p>

            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-block bg-burgundy text-cream-bright border-[3px] border-burgundy px-9 py-3.5 font-bagel text-[18px] tracking-[1px] rounded-full cursor-pointer shadow-chunk-5 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-chunk-7 active:translate-x-[2px] active:translate-y-[2px] active:shadow-[2px_2px_0_#3D1220] transition-[transform,box-shadow] duration-150"
            >
              FOLLOW @ISWISPOD
            </a>

            <div className="mt-8 text-[13px] text-burgundy-faint">
              <Link
                to="/"
                className="text-pink-deep underline hover:text-burgundy font-semibold"
              >
                Send another one
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function DotI({ children }: { children: ReactNode }) {
  return (
    <span className="relative inline-block">
      <span
        className="absolute left-1/2 -translate-x-1/2 w-[7px] h-[7px] bg-pink rounded-full"
        style={{ top: '-10px' }}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}
